import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Region } from "../types";

const THUMB_WIDTH = 100;
const THUMB_HEIGHT = 74;

interface RegionThumbnailProps {
  region: Region;
  imageSrc: string;
}

export const RegionThumbnail = ({ region, imageSrc }: RegionThumbnailProps) => {
  const { t } = useTranslation();

  // CSS-only crop: scale the full image up so the selected region fills the
  // thumb box, then offset it so the region's top-left aligns with the box.
  const imgWidth = THUMB_WIDTH / (region.width / 100);
  const imgHeight = THUMB_HEIGHT / (region.height / 100);
  const imgLeft = -(region.x / 100) * imgWidth;
  const imgTop = -(region.y / 100) * imgHeight;

  return (
    <Box sx={{ textAlign: "center", flexShrink: 0 }}>
      <Box
        sx={{
          width: THUMB_WIDTH,
          height: THUMB_HEIGHT,
          border: `2px solid ${region.color}`,
          borderRadius: "var(--card-extra-small, 4px)",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "var(--alias-surface-low, #f2f2f2)",
        }}
      >
        <img
          src={imageSrc}
          alt={`Region ${region.label}`}
          draggable={false}
          style={{
            position: "absolute",
            width: imgWidth,
            height: imgHeight,
            left: imgLeft,
            top: imgTop,
            maxWidth: "none",
            objectFit: "fill",
            pointerEvents: "none",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing4, 4px)",
          marginTop: "var(--spacing6, 6px)",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: region.color,
          }}
        />
        <Typography
          sx={{
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            fontWeight: 500,
          }}
        >
          {t("ldp_explore_image_region_label", { label: region.label })}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: "var(--footnote-f1-10-regular-size, 11px)",
          color: "var(--alias-text-on-white-disable, #999)",
          marginTop: "var(--spacing2, 2px)",
        }}
      >
        {Math.round(region.width)}% &times; {Math.round(region.height)}%
      </Typography>
    </Box>
  );
};
